/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { parseQueryString } from "utils/url";
import SectionTabs from "components/SectionTabs";
import FiltersForm from "./Filters";
import ResultsTable from "./ResultsTable";
import ReportsList from "./Reports";
import ReportConfigModal from "./Reports/ReportConfigModal";
import { useEmailLogContext } from "../EmailLogProvider";
import {
  cancelReport,
  createLogsReport,
  getAllEmailLogReports,
  getEmailLogReportById,
  searchLogs,
} from "../api";

import {
  EmailFlowLogReport,
  EmailFlowLogReportRequest,
  EmailFlowLogsSearchRequest,
  EmailLogReportsResponse,
} from "@dashboard-v3/api";
import { Filters, State, Tab } from "../types";

const RESULTS_LIMIT = 100;

type QueryStringFilters = Omit<Filters, "errorCodes"> & { errorCodes: string };

export default function EmailFlowLogs() {
  const { t } = useTranslation("emailFlowLogs");
  const { enqueueSnackbar } = useSnackbar();
  const { state, updateState } = useEmailLogContext();

  useEffect(() => {
    const { logs } = state;
    const { search } = window.location;

    if (search && !logs.length) {
      const qs = parseQueryString<QueryStringFilters>(search);
      const { fromDate, toDate, fromTime, toTime, errorCodes, ...rest } = qs;
      const from = dayjs(`${fromDate} ${fromTime}`);
      const to = dayjs(`${toDate} ${toTime}`);

      onSearchLogs({
        ...rest,
        dateFrom: from.utc().valueOf(),
        dateTo: to.utc().valueOf(),
        ...(rest.status && { status: rest.status }),
        ...(errorCodes && { errorCodes: errorCodes.split(",") }),
      });
    }
  }, []);

  useEffect(() => {
    async function initReports() {
      updateState({ status: "FETCHING_REPORTS" });
      try {
        const res = await getAllEmailLogReports(5, 1);
        updateState({ reportsData: res });
        handlePendingReports(res.reports);
      } catch (e) {
        enqueueSnackbar(t("error.fetchTracingReports"), {
          variant: "error",
        });
      } finally {
        updateState({ status: "IDLE" });
      }
    }

    const {
      currentTab,
      reportsData: { reports },
    } = state;

    if (currentTab === "REPORTS" && !reports.length) {
      initReports();
    }
  }, [state.currentTab]);

  const handlePendingReports = (r: EmailLogReportsResponse["reports"]) => {
    const pendingReport = r.find(r => r.status === "PENDING");
    if (pendingReport) {
      checkReportStatus(pendingReport.id);
    }
  };

  function checkReportStatus(id: string) {
    let status: EmailFlowLogReport["status"];

    updateState({ status: "GENERATING_REPORT" });

    const interval = setInterval(async () => {
      const report = await getEmailLogReportById(id);
      status = report.status;

      if (status !== "PENDING") {
        const { reportsData } = state;
        const filteredList = reportsData.reports.filter(
          r => r.id !== report.id
        );

        updateState({
          status: "IDLE",
          reportsData: { ...reportsData, reports: [report, ...filteredList] },
        });

        clearInterval(interval);
      }
    }, 2000);
  }

  const getTabs = () => {
    return ["SEARCH", "REPORTS"].map(id => {
      return { id, label: t(`sectionTab.${id}`) };
    });
  };

  async function onSearchLogs(criteria: EmailFlowLogsSearchRequest) {
    const newCriteria = { ...criteria, limit: RESULTS_LIMIT, offset: 0 };
    updateState({
      status: "SEARCHING",
      searchCriteria: newCriteria,
      logs: [],
      page: 0,
      rowsPerPage: 100,
      filtersExpanded: false,
    });

    try {
      const res = await searchLogs(newCriteria);
      updateState({ logs: res.logs, hasMore: res.hasMore });
    } catch (error: unknown) {
      enqueueSnackbar(t("search.errors.fetchSearchResults"), {
        variant: "error",
      });
    } finally {
      updateState({ status: "IDLE" });
    }
  }

  async function onGetMoreLogs() {
    const { searchCriteria } = state;
    const offset = searchCriteria.offset + RESULTS_LIMIT;
    const newCriteria = { ...searchCriteria, offset };
    updateState({ status: "GETTING_MORE_LOGS" });

    try {
      const res = await searchLogs(newCriteria);

      updateState({
        logs: state.logs.concat(res.logs),
        hasMore: res.hasMore,
        searchCriteria: newCriteria,
      });
    } catch (error: unknown) {
      enqueueSnackbar(t("search.errors.fetchSearchResults"), {
        variant: "error",
      });
    } finally {
      updateState({ status: "IDLE" });
    }
  }

  async function getReportsByPage(page: number) {
    updateState({ status: "FETCHING_REPORTS" });
    try {
      const res = await getAllEmailLogReports(5, page);
      updateState({ reportsData: res });
      handlePendingReports(res.reports);
    } catch (e) {
      enqueueSnackbar(t("error.fetchTracingReports"), {
        variant: "error",
      });
    } finally {
      updateState({ status: "IDLE" });
    }
  }

  async function createReport(fields: EmailFlowLogReportRequest["fields"]) {
    updateState({
      status: "GENERATING_REPORT",
      filtersExpanded: false,
    });

    try {
      await createLogsReport({ ...state.searchCriteria, fields });
      await getReportsByPage(1);
    } catch (e) {
      enqueueSnackbar(t("downloadReportError"), { variant: "error" });
    } finally {
      updateState({ status: "IDLE" });
    }
  }

  const onCancelReport = async (id: string) => {
    const { reportsData } = state;
    const report = await cancelReport(id);

    const filteredList = reportsData.reports.filter(r => r.id !== report.id);
    updateState({
      status: "IDLE",
      reportsData: { ...reportsData, reports: [report, ...filteredList] },
    });
  };

  const onReportsPageChange = async (page: number) => {
    await getReportsByPage(page);
  };

  const onSubmit = async (criteria: EmailFlowLogsSearchRequest) => {
    const { currentTab } = state;
    if (currentTab === "SEARCH") onSearchLogs(criteria);
    if (currentTab === "REPORTS") {
      updateState({ searchCriteria: criteria, showReportConfig: true });
    }
  };

  return (
    <>
      <SectionTabs
        tabs={getTabs()}
        showTab={state.currentTab}
        onClick={val =>
          updateState({ filtersExpanded: true, currentTab: val as Tab })
        }
      />
      <FiltersForm
        tab={state.currentTab}
        onSubmit={onSubmit}
        expanded={state.filtersExpanded}
        disableSubmit={isSubmitDisabled(state)}
        onExpand={() =>
          updateState({ filtersExpanded: !state.filtersExpanded })
        }
      />
      {state.currentTab === "SEARCH" && (
        <ResultsTable
          onGetMore={onGetMoreLogs}
          searchState={state}
          updateState={updateState}
        />
      )}
      {state.currentTab === "REPORTS" && (
        <ReportsList
          data={state.reportsData}
          page={state.reportsData.currentPage}
          loading={state.status === "FETCHING_REPORTS"}
          onPageChange={onReportsPageChange}
          onCancelReport={onCancelReport}
        />
      )}
      {state.showReportConfig && (
        <ReportConfigModal
          isOpen={state.showReportConfig}
          dateFrom={state.searchCriteria.dateFrom}
          onClose={() => updateState({ showReportConfig: false })}
          onConfirm={fields => createReport(fields)}
        />
      )}
    </>
  );
}

function isSubmitDisabled(state: State) {
  const { status, currentTab } = state;

  if (currentTab === "SEARCH") {
    const disableStatuses = ["SEARCHING", "GETTING_MORE_LOGS"];
    return disableStatuses.includes(status);
  }

  if (currentTab === "REPORTS") {
    const disableStatuses = ["FETCHING_REPORTS", "GENERATING_REPORT"];
    return disableStatuses.includes(status);
  }

  return false;
}
